// Country availability gate.
//
// Before the user can use Legendaryum (even before SSO login), the web app asks
// the public geo endpoint (`GET public/geo`, unauthenticated) which reads the
// infra-injected geo header and checks the Country Registry (countries enabled
// from front-administrator). If the viewer's country is disabled, the app routes
// to a "not available yet" screen instead of letting them in.
//
// The result is resolved once per page load and cached, so navigation stays
// instant after the first check.
import { socialService } from '@features/social/api/social.service';
import type { DetectedGeo } from '@features/social/api/social.service.types';

let cachedGeo: DetectedGeo | null = null;
let resolved = false;

/** Resolve (and cache) the viewer's detected geo for this page load. */
export async function resolveDetectedGeo(): Promise<DetectedGeo | null> {
  if (resolved) return cachedGeo;
  try {
    const { data } = await socialService.getDetectedCountry();
    cachedGeo = data;
  } catch {
    // Fail-open on endpoint error (see isAppAvailable).
    cachedGeo = null;
  }
  resolved = true;
  return cachedGeo;
}

/**
 * Whether the app is available for the viewer.
 *
 * Fail-open: allow when the country is unknown (header missing / geo could not be
 * resolved) so an infra hiccup never locks everyone out. Block only a country that
 * is confirmed disabled in the registry. Mirrors the LOCAL/GLOBAL campaign rule.
 */
export function isAppAvailable(geo: DetectedGeo | null): boolean {
  if (!geo?.country) return true;
  return geo.isEnabled;
}

/** Last resolved geo (for the unavailable screen to name the country). */
export function getCachedGeo(): DetectedGeo | null {
  return cachedGeo;
}
